import { Injectable } from "@angular/core";
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse
} from "@angular/common/http";
import { AuthService } from "../services/auth/auth.service";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { catchError, filter, finalize, switchMap, take } from "rxjs/operators";
import { NavController } from "@ionic/angular";

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> =
        new BehaviorSubject<any>(null);

    constructor(
        public authService: AuthService,
        public navController: NavController
    ) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        if (request.url.endsWith("v2/auth/login")) {
            return next.handle(this.addTwoFaToken(request));
        }

        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                if (request.url.endsWith("refresh-token")) {
                    this.authService.logout();
                    this.isRefreshing = false;

                    console.error(error);
                    return throwError(error);
                }

                if (request.url.endsWith("logout")) {
                    this.isRefreshing = false;

                    console.error(error);
                    return throwError(error);
                }

                if (
                    error.status === 403 &&
                    [
                        "token-type-not-present",
                        "token-type-not-correct"
                    ].includes(error.error?.message)
                ) {
                    this.authService.completeLogout();
                    this.isRefreshing = false;

                    console.error(error);
                    return throwError(error);
                }

                if (![401, 455, 456].includes(error.status)) {
                    console.error(error);
                    return throwError(error);
                }

                if (error.status === 455) {
                    this.navController.navigateRoot("/profile");
                    console.error(error);
                    return throwError(error);
                }

                if (error.status === 456) {
                    this.authService.completeLogout();
                    console.error(error);
                    return throwError(error);
                }

                if (this.isRefreshing) {
                    return this.refreshTokenSubject.pipe(
                        filter((token) => token !== null),
                        take(1),
                        switchMap((token) =>
                            token
                                ? next.handle(this.addToken(request))
                                : throwError(error)
                        )
                    );
                } else {
                    this.isRefreshing = true;
                    this.refreshTokenSubject.next(null);

                    return this.authService.refreshToken().pipe(
                        switchMap((data: any) => {
                            this.refreshTokenSubject.next(data.token);

                            return next.handle(this.addToken(request));
                        }),
                        catchError((refreshError) => {
                            // Release the queued requests instead of leaving
                            // them waiting for a token that never arrives.
                            this.refreshTokenSubject.next(false);

                            return throwError(refreshError);
                        }),
                        // Without this the flag stays true after a failed
                        // refresh, leaving every next request waiting forever
                        // on a token that is never emitted.
                        finalize(() => {
                            this.isRefreshing = false;
                        })
                    );
                }
            })
        );
    }

    addToken(request) {
        const accessToken = this.authService.authState.getValue().token;

        if (!accessToken) {
            return request;
        }

        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${accessToken}`
            }
        });
    }

    addTwoFaToken(request: HttpRequest<any>) {
        const accessToken =
            this.authService.authState.getValue().twoFactorToken;

        if (!accessToken) {
            return request;
        }

        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${accessToken}`
            }
        });
    }
}
